import React, { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DASHBOARD_TITLES } from '../../../../constants/dashboard-constants';
import { Text, font } from '../../../../style-utils/text';
import { RightPointer } from '../../../icons/right-pointer';
import {
  DashboardLogout,
  LogoContainer,
  LogOutButton,
  MenuContainer,
  MenuItem,
  MenuAndLogoutWrappper,
  MenuTitle,
  LogoButton,
  LogoWordmark,
  MenuHint,
  MenuHintDismiss,
  MenuStrip,
  MoreButton,
  RailIndicator,
} from '../dashboard.styled';
import { IndicatorGroup } from '../../../../motion';
import { NavMoreSheet } from './nav-more-sheet';
import { LogoMark } from '../../../logo/logo-mark';
import { palette } from '../../../../style-utils/theme';
import { LogoutIcon } from '../../../icons/log-out';
import { NavbarContainer } from '../../../layout/layout-container.styled';
import { useAuth } from '../../../../context/auth-provider';
import { DashboardLogoArrow } from '../../../icons/dashboard-logo-arrow';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { usePortfolioDisclosure } from '../../../hooks/use-portfolio-disclosure';
import { staysIsOn, useStaysEntitlement } from '../../../hooks/use-stays-entitlement';
import { deskInPlan, OrgProducts, ProductKey, shellProducts } from '../../../../utils/products';

// One entry per nav item, in PRIORITY ORDER (owner ruling R-13).
//
// The order is load-bearing on a phone and only on a phone: the fixed bottom bar
// takes the FIRST FOUR entries the signed-in role can actually see, and
// everything after that goes into the More sheet. Nine destinations at 16px in a
// 390px bar was ~990px of content inside a 72px `overflow: hidden` container
// whose inner strip scrolled with its scrollbar suppressed — so five of the nine
// were unreachable and nothing said they existed.
//
// Taking the first four VISIBLE entries rather than the first four entries is
// what makes a restricted role degrade to a shorter bar instead of showing gaps:
// a concierge who cannot see Shifts gets Units in that slot, not a hole.
//
// The vertical rail above md still renders all of them, in this same order — it
// is a column with room for nine. Reordering this array is the whole knob, and
// reordering it after real use is expected.
//
// `slug` is what the active pill matches on and what the heading shows; `path`
// is where the item navigates. The two were the same string while every label
// was a single lowercase word, but "Staff Access" lives at /dashboard/staff-access,
// so deriving the route from the label by lowercasing no longer holds.
//
// `permission` is optional: an entry without one is visible to anyone who can
// reach the dashboard at all (the pre-existing items), and an entry with one is
// hidden unless the server handed the user that exact permission string.
//
// `product` (Independent Stays IS1): which product the destination belongs to.
// 'desk' entries are hidden in an organisation without Lobbero Desk, whatever
// the permission says — Home and Residents carry no permission at all, and
// Shifts is gated on user:read, which a Stays-only owner still holds.
export interface MenuEntry {
  label: DASHBOARD_TITLES;
  slug: string;
  path: string;
  permission?: string;
  product: ProductKey;
}

const MENU_ENTRIES: MenuEntry[] = [
  { label: DASHBOARD_TITLES.HOME, slug: 'home', path: '/dashboard', product: 'desk' },
  {
    label: DASHBOARD_TITLES.BUILDINGS,
    slug: 'buildings',
    path: '/dashboard/buildings',
    // Independent Stays IS2: without Desk these rows are Properties (in Stays).
    product: 'buildings',
  },
  {
    // WP-31. The label has said Residents since dashboard-constants.tsx:22; the
    // slug and the path now say it too. The ENUM KEY stays TENANTS — see the
    // comment on it for why.
    label: DASHBOARD_TITLES.TENANTS,
    slug: 'residents',
    path: '/dashboard/residents',
    product: 'desk',
  },
  {
    // `user:read`, the SAME permission the two routes behind this screen are
    // gated on. A role list would drift from the server's rule the first time
    // the matrix changes; the capability cannot.
    label: DASHBOARD_TITLES.SHIFTS,
    slug: 'shifts',
    path: '/dashboard/shifts',
    permission: PERMISSIONS.USER_READ,
    product: 'desk',
  },
  /* ── everything below here is the More sheet on a phone (R-13) ───────── */
  {
    label: DASHBOARD_TITLES.UNITS,
    slug: 'units',
    path: '/dashboard/units',
    permission: PERMISSIONS.UNIT_READ,
    product: 'buildings',
  },
  {
    label: DASHBOARD_TITLES.USERS,
    slug: 'users',
    path: '/dashboard/users',
    /* THE SECOND UNGATED ENTRY, closed with Billing's.
       `GET /dashboard/users` is `requirePermission('user:read')`, and this row
       carried no `permission` — so a role without it would be offered a screen
       whose every request 403s, exactly as a property manager was offered
       Billing (`scripts/wp/verify-roles-live.js`).
       It is unreachable TODAY because every role that lands on the staff shell
       holds `user:read`, which is why nothing was ever observed. The bar test's
       own comment is the argument for fixing it anyway: "a concierge is included
       even though they land on the desk shell today: the rule must degrade for
       them, and 'nobody has that shape right now' is not a reason for it not
       to." */
    permission: PERMISSIONS.USER_READ,
    product: 'core',
  },
  {
    label: DASHBOARD_TITLES.PORTFOLIOS,
    slug: 'portfolios',
    path: '/dashboard/portfolios',
    permission: PERMISSIONS.PORTFOLIO_READ,
    product: 'core',
  },
  {
    label: DASHBOARD_TITLES.STAFF_ACCESS,
    slug: 'staff-access',
    path: '/dashboard/staff-access',
    permission: PERMISSIONS.SCOPE_READ,
    product: 'desk',
  },
  /* ANALYTICS AND REPORTS ARE GONE FROM THE MENU. Both routes still resolve —
     an emailed link and an old bookmark must not 404 — and both render the
     Dashboard, which is the entry at the top.

     THE DASHBOARD CARRIES NO `permission`, and that is deliberate. The old
     Analytics entry was gated on `analytics:read` so a concierge would never see
     a tile whose every request would 403. This screen is not that: without
     `analytics:read` it hides the Trends tab, fires ZERO analytics requests, and
     still shows the activity feed and the records — which a concierge can read
     and does need. Gating the whole entry would take those away from them.
     `dashboard-screen.test.tsx` asserts the no-request half. */
  {
    label: DASHBOARD_TITLES.BILLING,
    slug: 'billing',
    path: '/dashboard/billing',
    /* BILLING WAS THE ONE GATED SECTION WITH NO GATE.
       Found by the first non-admin pass anybody has run
       (`scripts/wp/verify-roles-live.js`): a PROPERTY MANAGER was offered this
       entry and `/dashboard/billing` rendered its heading, while every billing
       endpoint behind it correctly answered 403 — `billing:read` is Owner-only.
       So the screen was reachable and could not load, which is the worst of both
       readings: it is not a security hole, it is a promise the API refuses to
       keep.

       This is NOT the Dashboard's deliberate no-permission case above. That one
       degrades — without `analytics:read` it hides a tab, fires no requests and
       still shows what a concierge can read. Billing has nothing to degrade to:
       every panel on it needs `billing:read`. */
    permission: PERMISSIONS.BILLING_READ,
    product: 'core',
  },
  /* VISITOR PASSES (resident-portal Plan 3, RP-3.7). APPENDED, because this
     array's order is the phone bar's priority (four + More) and a new section
     does not outrank the ones people already use. visitorpass:read is the gate
     of GET /buildings/:b/passes, the list this screen reads — held by the
     owner, property managers and the desk. */
  {
    label: DASHBOARD_TITLES.VISITOR_PASSES,
    slug: 'visitor-passes',
    path: '/dashboard/visitor-passes',
    permission: PERMISSIONS.VISITORPASS_READ,
    product: 'desk',
  },
  /* LOBBERO STAYS (ai-plans/stays plan S1). APPENDED for the same reason — on a
     phone it lands in More. Two gates: stays:read (owner, property manager) AND
     the organisation's Stays flag, read from GET /stays/entitlement in the
     filter below — an organisation without Stays never sees the entry. */
  {
    label: DASHBOARD_TITLES.STAYS,
    slug: 'stays',
    path: '/dashboard/stays',
    permission: PERMISSIONS.STAYS_READ,
    product: 'stays',
  },
];

// Which section a URL belongs to. Longest / most specific keyword first so a
// sub-route never resolves to the wrong section. The roster is a Units screen
// reached from the Units list, so it keeps the Units item lit rather than
// dropping the nav back to Home.
const SECTION_KEYWORDS: { slug: string; keywords: string[] }[] = [
  // FIRST, because the match is `path.includes` and first wins: a later Stays
  // path (/dashboard/stays/<id>/…) may contain any other section's word.
  { slug: 'stays', keywords: ['/dashboard/stays'] },
  { slug: 'visitor-passes', keywords: ['visitor-passes'] },
  { slug: 'buildings', keywords: ['buildings'] },
  { slug: 'portfolios', keywords: ['portfolios'] },
  { slug: 'staff-access', keywords: ['staff-access'] },
  { slug: 'units', keywords: ['units', 'roster'] },
  { slug: 'users', keywords: ['users'] },
  // BOTH SPELLINGS. '/dashboard/tenants' redirects to '/dashboard/residents'
  // (dashboard.tsx), but the pill is derived on the render BEFORE the redirect
  // commits, and the nav flashing Home on the way through reads as a page that
  // does not know where it is.
  { slug: 'residents', keywords: ['residents', 'tenants'] },
  { slug: 'shifts', keywords: ['shifts'] },
  /* THE FOUR OLD PATHS ALL LIGHT THE ONE ENTRY. Somebody arriving on a saved
     /dashboard/analytics link has to see the nav agree with the screen they are
     looking at; a lit item that is not the current screen, and no lit item at
     all, both read as a broken page. */
  {
    slug: 'home',
    keywords: ['analytics', 'reports', 'view-edit-export', 'home'],
  },
  { slug: 'billing', keywords: ['billing'] },
];

const entryBySlug = (slug: string): MenuEntry | undefined =>
  MENU_ENTRIES.find(entry => entry.slug === slug);

/* HOW MANY DESTINATIONS STAY ON THE BAR (owner ruling R-13).
 *
 * Four, plus More. Not "as many as fit": a bar whose length depends on the
 * label widths of whichever role signed in is a bar nobody can learn, and the
 * measurement it would need does not exist until after first paint. Four is a
 * decision, and it is one line to change.
 *
 * Exported for the test, which asserts the split rather than restating it. */
export const BAR_SLOTS = 4;

/** What the rail offers, in order, for one session (Independent Stays IS1).
 *
 *  ONE FILTER for the bar, the More sheet and the vertical rail. An entry
 *  needs its permission (if any) and its product: a 'desk' entry is hidden
 *  without Lobbero Desk. In a Stays-only organisation Stays is the home, so it
 *  moves to the FRONT — on a phone that puts it in the bar's first four
 *  instead of the More sheet. A missing `products` (an older server) is Desk.
 *  `extra` is the per-entry narrowing the component adds (portfolio
 *  disclosure, the Stays flag). Exported for the test. */
export const visibleMenuEntries = (
  permissions: string[],
  products: OrgProducts | null | undefined,
  extra: (entry: MenuEntry) => boolean = () => true
): MenuEntry[] => {
  const desk = products?.desk !== false;
  const visible = MENU_ENTRIES.filter(entry => {
    if ((entry.product === 'desk' || entry.product === 'buildings') && !desk) return false;
    if (entry.permission && !can(permissions, entry.permission)) return false;

    return extra(entry);
  });
  if (desk) return visible;

  return [
    ...visible.filter(entry => entry.slug === 'stays'),
    ...visible.filter(entry => entry.slug !== 'stays'),
  ];
};

/** The bar's four, and everything the More sheet gets — from ONE filtered list,
 *  so the two can never disagree about what the reader is allowed to see. */
export const splitForBar = (
  visible: MenuEntry[]
): { primary: MenuEntry[]; overflow: MenuEntry[] } => ({
  primary: visible.slice(0, BAR_SLOTS),
  overflow: visible.slice(BAR_SLOTS),
});

export const dashboardSectionForPath = (path: string): string => {
  if (path === '/dashboard' || path === '/dashboard/') return 'home';

  const section = SECTION_KEYWORDS.find(candidate =>
    candidate.keywords.some(keyword => path.includes(keyword))
  );

  /* '' FOR AN ADDRESS THAT MATCHES NOTHING, not 'home' (WP-31). An unknown
     /dashboard/* path now renders a 404 rather than the Dashboard, and a lit
     Home pill above a "that page is not here" is the same misdirection one layer
     up. Nothing else reaches this line: '/dashboard' and '/dashboard/' return
     above, and every real section is a keyword. */
  return section?.slug || '';
};

export const dashboardTitleForPath = (path: string): string => {
  const section = entryBySlug(dashboardSectionForPath(path));

  return section?.label || DASHBOARD_TITLES.HOME;
};

export const DashboardMainMenu: React.FC = () => {
  const { logout, permissions, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentSection = dashboardSectionForPath(location.pathname);

  // PROGRESSIVE DISCLOSURE (owner decision Q-5). Portfolios group buildings, so
  // the item is meaningless to an admin who has one; it appears on the second.
  // Permission is still the outer gate — this only narrows further, never wider,
  // so nobody sees an item they could not already reach.
  const { showPortfolios, showHint, dismissHint } = usePortfolioDisclosure();

  // Lobbero Stays: the entry needs the ORGANISATION's flag as well as the
  // permission. Asked only by a session that holds stays:read; not loaded and
  // any error read as off.
  const staysOn = staysIsOn(
    useStaysEntitlement({ enabled: can(permissions, PERMISSIONS.STAYS_READ) }).state
  );

  // shellProducts: a co-host's rail is the Stays-only rail (IS5).
  const visibleEntries = visibleMenuEntries(permissions, shellProducts(user), entry => {
    if (entry.slug === 'portfolios') return showPortfolios;
    if (entry.slug === 'stays') return staysOn;

    return true;
  });

  /* R-13. `overflow` is what the More sheet holds on a phone; the SAME entries
     still render in the vertical rail above md, where there is room for all of
     them. One filtered list feeds both, so the sheet cannot offer a destination
     the bar was not allowed to show. */
  const { primary, overflow } = splitForBar(visibleEntries);
  const [moreOpen, setMoreOpen] = useState(false);
  const closeMore = useCallback(() => setMoreOpen(false), []);

  // The strip is the scroller; the shade that reports its position is measured
  // off this (WP-34's ScrollStrip, shared).
  const stripRef = useRef<HTMLDivElement>(null);

  const getPathFromMenu = (entry: MenuEntry) => {
    return navigate(entry.path);
  };

  const goToSlug = (slug: string) => {
    const entry = entryBySlug(slug);

    setMoreOpen(false);
    if (entry) navigate(entry.path);
  };

  // Single shared logout (revokes this device's session server-side, clears the
  // httpOnly cookies, resets auth state and routes to login).
  const logOut = () => logout();

  return (
    <NavbarContainer dashboard={true} id="dashboard-menu-container">
      <LogoContainer id="logo-container">
        <LogoButton
          type="button"
          aria-label="Open dashboard home"
          // The first entry this session sees: Dashboard for a Desk
          // organisation, Stays for a Stays-only one (IS1).
          onClick={() => getPathFromMenu(visibleEntries[0] || MENU_ENTRIES[0])}
        >
          <LogoMark size={32} aria-hidden="true" />
          <LogoWordmark>Lobbero</LogoWordmark>
        </LogoButton>
        <DashboardLogoArrow />
      </LogoContainer>
      {/* THE PRIMARY NAVIGATION IS A LANDMARK, and until WP-39 it was not.
          The product had exactly two <nav> elements — the account screen's
          section list and the analytics breadcrumb — and neither was this. A
          screen-reader user could not jump to the main navigation on any
          screen; they had to tab through it. `as` only: NavbarContainer keeps
          every rule it had, and the logo above stays outside the landmark so
          the landmark contains navigation and nothing else. */}
      <MenuAndLogoutWrappper as="nav" aria-label="Main">
        {/* One IndicatorGroup per rail: the accent bar's layoutId is scoped to
            it, so the desk rail's bar and this one never try to share. */}
        <IndicatorGroup id="dashboard-rail">
        <MenuStrip scroller={stripRef}>
          <MenuContainer id="dashboard-main-menu" ref={stripRef}>
            <MenuTitle>
              <Text
                mb={0}
                mt={0}
                fontFamily={font.family}
                fontSize={font.size.xxs}
                fontWeight={600}
                color={palette.slateLight}
                style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
              >
                Main menu
              </Text>
            </MenuTitle>
            {visibleEntries.map(e => {
              const isCurrent = currentSection === e.slug;
              // Rendered as a sibling of the item it explains, so it cannot
              // appear detached from the thing that just showed up. It is
              // `display: none` below md — see the note on MenuHint.
              const hint = e.slug === 'portfolios' && showHint;
              /* IN THE TREE, OUT OF THE BAR. Everything past the fourth visible
                 entry is a More-sheet destination on a phone and a rail item
                 above md; `railOnly` is that one difference, and it is a media
                 query rather than a width read in JS. */
              const railOnly = !primary.some(entry => entry.slug === e.slug);

              return (
                <React.Fragment key={e.slug}>
                  <MenuItem
                    type="button"
                    active={isCurrent}
                    railOnly={railOnly}
                    /* The media query is what actually hides it; this is the
                       seam a test can read, because jsdom resolves no media
                       queries and an emotion class tells it nothing. The live
                       probe reads the computed display instead. */
                    data-rail-only={railOnly}
                    aria-current={isCurrent ? 'page' : undefined}
                    onClick={() => getPathFromMenu(e)}
                  >
                    {/* THE BAR MOVES. Rendered only inside the current item;
                        framer carries it here from wherever it was. */}
                    {isCurrent && (
                      <RailIndicator
                        id="dashboard-rail"
                        data-active-indicator="true"
                      />
                    )}
                    <RightPointer fill={palette.iris} />
                    <Text
                      as="span"
                      mt={0}
                      mb={0}
                      fontFamily={font.family}
                      fontSize={font.size.s}
                      fontWeight={500}
                    >
                      {e.label}
                    </Text>
                  </MenuItem>
                  {hint && (
                    <MenuHint role="note">
                      <Text
                        as="span"
                        mt={0}
                        mb={0}
                        fontFamily={font.family}
                        fontSize={font.size.xs}
                        fontWeight={500}
                        color={palette.slate}
                      >
                        You have more than one building now. Portfolios let you
                        group them — it is optional, and it never changes your
                        units or billing.
                      </Text>
                      <MenuHintDismiss type="button" onClick={dismissHint}>
                        Got it
                      </MenuHintDismiss>
                    </MenuHint>
                  )}
                </React.Fragment>
              );
            })}
          </MenuContainer>
        </MenuStrip>
        </IndicatorGroup>
        {overflow.length > 0 && (
          <MoreButton
            type="button"
            data-testid="nav-more"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            /* LIT WHEN THE READER IS BEHIND IT. A bar whose four items are all
               unlit while the reader is plainly on a dashboard screen reads as
               a nav that does not know where it is — the same failure the
               section resolver exists to prevent one layer up. */
            active={overflow.some(entry => entry.slug === currentSection)}
            data-active={overflow.some(entry => entry.slug === currentSection)}
            onClick={() => setMoreOpen(open => !open)}
          >
            More
          </MoreButton>
        )}
        <NavMoreSheet
          open={moreOpen}
          entries={overflow.map(e => ({ label: e.label, slug: e.slug }))}
          currentSlug={currentSection}
          onSelect={goToSlug}
          onClose={closeMore}
        />
        <DashboardLogout>
          <LogOutButton type="button" onClick={() => logOut()}>
            <LogoutIcon />
            <Text
              as="span"
              mt={0}
              mb={0}
              color={font.color.purple}
              fontSize={font.size.s}
              fontWeight={500}
            >
              Logout
            </Text>
          </LogOutButton>
          <Text
            mt={0}
            mb={0}
            fontSize={font.size.xxs}
            color={palette.slateLight}
          >
            {/* Independent Stays IS1: a Stays-only account has no concierge. */}
            {deskInPlan(user) ? 'Lobbero concierge workspace' : 'Lobbero Stays workspace'}
          </Text>
        </DashboardLogout>
      </MenuAndLogoutWrappper>
    </NavbarContainer>
  );
};
